import React, { useCallback, useMemo, useState } from 'react';
import {
  ChevronDown, FolderOpen, ExternalLink, DollarSign, Loader as LoaderIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import EmptyState from './EmptyState';
import {
  useCategoriesGrouped,
  fetchCategoryChannels,
  mapFiltersToCategoryParams,
} from './hooks/useCategoryData';
import {
  formatK, getAvatarColor, getCategoryName, getInitials,
} from './utils';

/**
 * CategoryGroupedViewCompact — aba "Por Categoria" (Sprint 4). Substitui
 * CategoryGroupedView (que agrupava rawChannels client-side, sem paginação
 * nem filtros server-side).
 *
 * Diferenças:
 *  - Dados vêm de /by-category-grouped (headers + counts do banco inteiro) e
 *    /category-channels (canais paginados por categoria). Ambos já aplicam
 *    filtros invisíveis (subs>=100, num_uploads>=3) e excluem shorts.
 *  - Cards compactos (1 linha) no estilo da página legada.
 *  - Lazy-load por categoria (50 por página + "ver mais").
 *
 * Mapeamento front->backend dos filtros: ver mapFiltersToCategoryParams.
 * `categories[]` selecionadas são filtradas client-side aqui.
 */

const PAGE_SIZE = 50;

// Card compacto: avatar + nome + views/subs + $ + link externo.
const CompactChannelCard = ({ channel, isPrivacyMode, isVisited, onVisit }) => {
  const { t } = useTranslation();
  const {
    channel_id, title, thumbnail, subscribers, total_views,
    is_monetized, channel_url,
  } = channel;

  const handleOpen = () => { if (onVisit) onVisit(channel_id); };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: 12,
        height: '100%',
        boxSizing: 'border-box',
        background: 'var(--dp-bg-input)',
        border: '1px solid var(--dp-border-subtle)',
        borderRadius: 12,
        opacity: isVisited ? 0.6 : 1,
        fontFamily: 'var(--dp-font)',
      }}
    >
      {/* Avatar: initials como base; thumbnail por cima (cai pro initials no erro). */}
      <div style={{ position: 'relative', width: 44, height: 44, flexShrink: 0 }}>
        <div
          style={{
            width: 44, height: 44, borderRadius: '50%',
            background: getAvatarColor(channel_id),
            color: '#fff', fontSize: 14, fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          {isPrivacyMode ? '?' : getInitials(title)}
        </div>
        {!isPrivacyMode && thumbnail && (
          <img
            src={thumbnail}
            alt=""
            loading="lazy"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
            style={{
              position: 'absolute', inset: 0, width: 44, height: 44,
              borderRadius: '50%', objectFit: 'cover',
            }}
          />
        )}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span
            style={{
              flex: 1, minWidth: 0,
              fontSize: 13, fontWeight: 600, color: 'var(--dp-text-primary)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}
          >
            {isPrivacyMode ? '••••••••' : (title || '—')}
          </span>
          {/* Pacote 8E — simetrico ao ChannelCard: verde true, vermelho false,
              nada null. Sem tooltip por design (visao compacta sem hovers). */}
          {is_monetized === true && (
            <DollarSign size={12} style={{ color: 'var(--dp-green-500)', flexShrink: 0 }} />
          )}
          {is_monetized === false && (
            <DollarSign size={12} style={{ color: 'var(--dp-red-500)', flexShrink: 0 }} />
          )}
        </div>
        <div
          style={{
            display: 'flex', gap: 12, marginTop: 4,
            fontSize: 11, color: 'var(--dp-text-secondary)',
          }}
        >
          <span>{isPrivacyMode ? '•••' : formatK(total_views)} {t('viralNiches.views_label')}</span>
          <span>{isPrivacyMode ? '•••' : formatK(subscribers)} {t('viralNiches.subs_label')}</span>
        </div>
      </div>

      <a
        href={isPrivacyMode ? undefined : (channel_url || '#')}
        target="_blank"
        rel="noopener noreferrer"
        onClick={isPrivacyMode ? (e) => e.preventDefault() : handleOpen}
        aria-label={t('viralNiches.view_channel')}
        style={{
          flexShrink: 0,
          width: 30, height: 30, borderRadius: 8,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(13,174,255,0.10)',
          color: 'var(--dp-blue-500)',
          cursor: isPrivacyMode ? 'not-allowed' : 'pointer',
          opacity: isPrivacyMode ? 0.5 : 1,
        }}
      >
        <ExternalLink size={15} />
      </a>
    </div>
  );
};

const CategoryHeader = ({ label, isOpen, onToggle, count, totalViews }) => (
  <button
    type="button"
    onClick={onToggle}
    style={{
      display: 'flex', alignItems: 'center', gap: 12, width: '100%',
      padding: 14, background: 'transparent', border: 'none',
      color: 'var(--dp-text-primary)', cursor: 'pointer',
      fontFamily: 'var(--dp-font)', textAlign: 'left',
    }}
  >
    <div
      style={{
        width: 32, height: 32, borderRadius: 8, flexShrink: 0,
        background: 'var(--dp-blue-subtle)', color: 'var(--dp-blue-500)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <FolderOpen size={16} />
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div
        style={{
          fontSize: 14, fontWeight: 600,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 11, color: 'var(--dp-text-tertiary)', marginTop: 2 }}>
        {count} · {formatK(totalViews)} views
      </div>
    </div>
    <ChevronDown
      size={16}
      style={{
        color: 'var(--dp-text-tertiary)', flexShrink: 0,
        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
        transition: 'transform 0.18s',
      }}
    />
  </button>
);

const CategorySection = ({ category, categoryParams, isPrivacyMode, visitedList, onVisit }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [channels, setChannels] = useState([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const loadMore = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await fetchCategoryChannels(
        category.name, offset, PAGE_SIZE, categoryParams,
      );
      const fetched = Array.isArray(data?.channels) ? data.channels : [];
      setChannels((prev) => [...prev, ...fetched]);
      setOffset((prev) => prev + fetched.length);
      setHasMore(!!data?.has_more);
    } catch (err) {
      setError(true);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category.name, offset, categoryParams]);

  const handleToggle = () => {
    const next = !isOpen;
    setIsOpen(next);
    if (next && channels.length === 0 && !loading) {
      loadMore(); // carrega a 1a página ao abrir
    }
  };

  const remaining = Math.max(0, (category.channel_count || 0) - channels.length);

  return (
    <div
      style={{
        background: 'var(--dp-bg-card)',
        border: '1px solid var(--dp-border-subtle)',
        borderRadius: 12,
        overflow: 'hidden',
      }}
    >
      <CategoryHeader
        label={getCategoryName(category.name, t) || category.name}
        isOpen={isOpen}
        onToggle={handleToggle}
        count={category.channel_count}
        totalViews={category.total_views}
      />
      {isOpen && (
        <div
          style={{
            padding: 16,
            borderTop: '1px solid var(--dp-border-subtle)',
            background: 'var(--dp-bg-secondary)',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
              gap: 10,
              alignItems: 'stretch',
            }}
          >
            {channels.map((ch, idx) => (
              <div
                key={ch.channel_id}
                className="dp-card-stagger-in"
                style={{ animationDelay: `${(idx % 3) * 60}ms` }}
              >
                <CompactChannelCard
                  channel={ch}
                  isPrivacyMode={isPrivacyMode}
                  isVisited={Array.isArray(visitedList) && visitedList.includes(ch.channel_id)}
                  onVisit={onVisit}
                />
              </div>
            ))}
          </div>

          {channels.length === 0 && loading && (
            <div style={{ padding: 16, textAlign: 'center', color: 'var(--dp-text-tertiary)', fontSize: 12 }}>
              {t('viralNiches.loading_more')}
            </div>
          )}

          {error && (
            <div style={{ padding: 12, textAlign: 'center' }}>
              <button type="button" className="dp-btn-secondary" onClick={loadMore}>
                {t('viralNiches.load_error_retry')}
              </button>
            </div>
          )}

          {hasMore && channels.length > 0 && !error && (
            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <button
                type="button"
                onClick={loadMore}
                disabled={loading}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '7px 14px', borderRadius: 8,
                  background: 'transparent',
                  border: '0.5px solid var(--dp-border-medium)',
                  color: 'var(--dp-blue-500)', fontSize: 12, fontWeight: 500,
                  fontFamily: 'var(--dp-font)',
                  cursor: loading ? 'wait' : 'pointer',
                }}
              >
                {loading
                  ? (<><LoaderIcon size={13} className="dp-spin" /> {t('viralNiches.loading_more')}</>)
                  : (<><ChevronDown size={13} /> +{remaining}</>)}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const CategoryGroupedViewCompact = ({ filters, isPrivacyMode, visitedList, onVisit }) => {
  const { t } = useTranslation();
  const categoryParams = useMemo(() => mapFiltersToCategoryParams(filters), [filters]);
  const { categories, loading, error } = useCategoriesGrouped(categoryParams);

  // categories[] selecionadas no filtro → filtro client-side das seções
  // (o backend /by-category-grouped retorna TODAS as categorias).
  const selectedCats = Array.isArray(filters?.categories) ? filters.categories : [];
  const visibleCategories = useMemo(() => {
    if (!selectedCats.length) return categories;
    const set = new Set(selectedCats);
    return categories.filter((c) => set.has(c.name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, JSON.stringify(selectedCats)]);

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="dp-skeleton" style={{ height: 60, borderRadius: 12 }} />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="dp-card" style={{ padding: 24, textAlign: 'center' }}>
        <div style={{ color: 'var(--dp-text-primary)' }}>
          {t('viralNiches.load_error_title')}
        </div>
      </div>
    );
  }

  if (!visibleCategories.length) {
    return <EmptyState hasActiveFilters={selectedCats.length > 0} />;
  }

  // key inclui os params → quando os filtros mudam, cada CategorySection
  // remonta e re-carrega do offset 0 (evita canais defasados).
  const paramsKey = JSON.stringify(categoryParams);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {visibleCategories.map((cat) => (
        <CategorySection
          key={`${cat.name}|${paramsKey}`}
          category={cat}
          categoryParams={categoryParams}
          isPrivacyMode={isPrivacyMode}
          visitedList={visitedList}
          onVisit={onVisit}
        />
      ))}
    </div>
  );
};

export default CategoryGroupedViewCompact;
